import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/api";
import { useAuth } from "@/AuthContext";

/**
 * PermissionContext — loads `/api/me/permissions` and exposes:
 *   - can(module, action?)  — permission check
 *   - isSuperAdmin          — server-verified super_admin flag
 *   - forceChangePassword   — server-verified flag; app must gate all writes
 *   - roleCodes             — user's assigned role codes
 *   - reload()              — force refresh (used after password change / role change)
 */
const Ctx = createContext({
  ready: false,
  permissions: new Set(),
  can: () => false,
  isSuperAdmin: false,
  financeRestricted: false,
  financialSummaryRestricted: false,
  forceChangePassword: false,
  roleCodes: [],
  reload: () => {},
});

export function PermissionProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(new Set());
  const [ready, setReady] = useState(false);
  const [roleCodes, setRoleCodes] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [financeRestricted, setFinanceRestricted] = useState(false);
  const [financialSummaryRestricted, setFinancialSummaryRestricted] = useState(false);
  const [loadedUser, setLoadedUser] = useState(null);
  const [forceChangePassword, setForceChangePassword] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!user) {
      setPermissions(new Set()); setRoleCodes([]);
      setIsSuperAdmin(false); setForceChangePassword(false);
      setFinanceRestricted(false);
      setFinancialSummaryRestricted(false);
      setLoadedUser(null);
      setReady(false);
      return;
    }
    let cancelled = false;
    setPermissions(new Set());
    setRoleCodes([]);
    setIsSuperAdmin(false);
    setFinanceRestricted(false);
    setFinancialSummaryRestricted(false);
    setForceChangePassword(false);
    setReady(false);
    let requestId = 0;
    const refresh = () => {
      const current = ++requestId;
      return api.get("/me/permissions")
      .then(r => {
        if (cancelled || current !== requestId) return;
        setPermissions(new Set(r.data.permissions || []));
        setRoleCodes(r.data.role_codes || []);
        setIsSuperAdmin(!!r.data.is_super_admin);
        setFinanceRestricted(!!r.data.finance_restricted);
        setFinancialSummaryRestricted(!!r.data.financial_summary_restricted);
        setLoadedUser(user.id || user.email);
        setForceChangePassword(!!r.data.force_password_change);
        setReady(true);
      })
      .catch(() => {
        if (cancelled || current !== requestId) return;
        setPermissions(new Set()); setRoleCodes([]); setIsSuperAdmin(false);
        setFinanceRestricted(true);
        setFinancialSummaryRestricted(true);
        setLoadedUser(user.id || user.email); setReady(true);
      });
    };
    refresh();
    // Background refresh does not unmount forms every minute. Failure clears
    // grants; successful responses replace the complete snapshot atomically.
    const visible = () => { if (document.visibilityState === "visible") refresh(); };
    const timer = window.setInterval(visible, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [user, nonce]);

  const accessReady = ready && loadedUser === (user?.id || user?.email) && !!user;
  const can = (module, action) => {
    if (!accessReady || !module) return false;
    if (forceChangePassword && action && !["view", "view_financial"].includes(action)) return false;
    // Super admin bypass (also reflected in permissions payload as ["*"] expansion)
    if (isSuperAdmin) return true;
    if (action) return permissions.has(`${module}.${action}`);
    // No action given → check the user has ANY permission on this module
    const prefix = `${module}.`;
    for (const p of permissions) if (p.startsWith(prefix)) return true;
    return false;
  };

  const reload = () => setNonce(n => n + 1);

  return (
    <Ctx.Provider value={{ ready: accessReady, permissions: accessReady ? permissions : new Set(), can,
      roleCodes: accessReady ? roleCodes : [], isSuperAdmin: accessReady && isSuperAdmin,
      financeRestricted: accessReady && financeRestricted,
      financialSummaryRestricted: !accessReady || financialSummaryRestricted, forceChangePassword, reload }}>
      {children}
    </Ctx.Provider>
  );
}

export const usePerms = () => useContext(Ctx);

/** Guard component: <RequirePerm module="invoices" action="create">…</RequirePerm> */
export function RequirePerm({ module, action, children, fallback = null }) {
  const { can, ready } = usePerms();
  if (!ready) return null;
  if (!can(module, action)) return fallback;
  return children;
}
